<template>
  <div class="space-y-4">
    <CommentsPanel entity-type="Invoice" :entity-id="invoice.id" :entity="invoice" />

    <div class="grid gap-4 sm:grid-cols-3">
      <LabeledText :label="t('receiver')" class="sm:row-span-2">
        <span v-if="invoice.receiverData.company">
          {{ invoice.receiverData.company }}
          <br />
        </span>
        {{ invoice.receiverData.firstName }}
        {{ invoice.receiverData.lastName }}
        <br />
        {{ invoice.receiverData.street }}
        <br />
        {{ invoice.receiverData.zip }}
        {{ invoice.receiverData.city }}
        <template v-if="invoice.receiverData.vatId">
          <br />
          {{ t('vatId') }}: {{ invoice.receiverData.vatId }}
        </template>
      </LabeledText>
      <LabeledText :label="t('invoiceNumber')">{{
        invoice.completeInvoiceNumber ?? t('invoiceNrNotAvailable')
      }}</LabeledText>
      <LabeledText :label="t('invoiceDate')">
        {{ invoice.invoiceDate ? formatDate(invoice.invoiceDate, dateFormat) : '-' }}
      </LabeledText>
      <LabeledText :label="t('status')">
        <InvoiceStatusBadge :status="invoice.status" size="md" />
      </LabeledText>
      <LabeledText :label="t('invoiceType')">
        {{ t(`invoiceTypes.${invoice.type}`) }}
      </LabeledText>
      <LabeledText :label="t('counterpartyType.label')">
        {{ displayCounterpartyType ? t(`counterpartyTypes.${displayCounterpartyType}`) : '-' }}
      </LabeledText>
    </div>

    <InvoicePositions :invoice="invoice" />

    <div class="flex justify-end gap-4">
      <LabeledText :label="t('openBalance')">
        <div class="flex justify-end">
          <span
            v-currency="invoice.currentBalance"
            class="font-bold tabular-nums"
            :class="{
              'text-red-500': !isZero(invoice.currentBalance),
              'text-success': isZero(invoice.currentBalance),
            }"
          />
        </div>
      </LabeledText>
      <LabeledText v-if="$can('list', 'InvoiceAllocation')" :label="t('allocated')">
        <div class="flex justify-end">
          <span
            v-currency="allocatedAmount"
            class="font-bold tabular-nums"
            :class="{
              'text-success': isClose(allocatedAmount, invoice.totalPositionSum),
              'text-red-500': !isClose(allocatedAmount, invoice.totalPositionSum),
            }"
          />
        </div>
      </LabeledText>
    </div>

    <div v-if="invoice.comment" class="rounded-md border border-obsidian-200 p-4">
      <LabeledText :label="t('comment')">
        <span class="whitespace-pre-line">{{ invoice.comment ?? '-' }}</span>
      </LabeledText>
    </div>

    <Collapsible
      v-if="$can('list', 'InvoiceAllocation')"
      v-model="showAllocations"
      :title="t('ledgerAllocations')"
      variant="compact"
    >
      <div v-if="!showSelectLedgerEntries" class="space-y-4">
        <LedgerEntryAllocations :invoice-id="invoice.id" />
        <div class="flex items-center justify-end gap-4">
          <div class="font-bold">
            <div v-if="toAllocate !== 0">
              {{ t('openAmount') }} {{ formatCurrency(toAllocate) }}
            </div>
            <div v-else class="flex items-center gap-2">
              {{ t('allocatedEntirely') }}
              <div
                class="flex size-5 items-center justify-center rounded-full bg-green-700 text-white"
              >
                <CheckIcon class="size-4" />
              </div>
            </div>
          </div>
          <CVButton
            v-if="$can('create', 'InvoiceAllocation')"
            @click.prevent="showSelectLedgerEntries = true"
            >{{ t('allocateAmount') }}</CVButton
          >
        </div>
      </div>
      <div v-else class="mt-2 space-y-4">
        <AllocateToLedgerEntry
          v-if="bookingId"
          :invoice="invoice"
          :scope="{
            bookingId,
          }"
          @success="showSelectLedgerEntries = false"
          @cancel="showSelectLedgerEntries = false"
        />
        <p v-else class="text-sm text-obsidian-600">{{ t('noBookingForAllocation') }}</p>
      </div>
    </Collapsible>

    <div class="flex flex-wrap gap-4 pt-2">
      <CVButton outline @click="downloadInvoice(invoice)">{{ t('downloadInvoice') }}</CVButton>
      <CVButton outline @click="downloadInvoiceDirect(invoice)">{{
        t('downloadInvoiceDirect')
      }}</CVButton>
      <CVButton
        v-if="invoice.status !== InvoiceStatus.CANCELED"
        outline
        :is-loading="isSending"
        @click="sendInvoiceToCustomerWithAlert(invoice.id)"
      >
        {{ t('sendToCustomer') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import InvoiceStatusBadge from '@/components/badges/InvoiceStatusBadge.vue';
import type { Invoice } from '@/entities/invoices/invoice.entity';
import { InvoiceStatus } from '@/entities/invoices/invoice-status.enum';
import { formatDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';
import LabeledText from '@/components/LabeledText.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import InvoicePositions from '@/views/invoices/invoice/InvoicePositions.vue';
import { useInvoicePdfs } from '@/hooks/use-invoice-pdf';
import LedgerEntryAllocations from '../ledger-entries/LedgerEntryAllocations.vue';
import Collapsible from '../Collapsible.vue';
import { computed, ref } from 'vue';
import { sumBy } from 'lodash';
import { useInvoiceAllocations } from '@/queries/use-ledger';
import AllocateToLedgerEntry from '../ledger-entries/AllocateToLedgerEntry.vue';
import { formatCurrency } from '@/utils/format-numbers';
import { CheckIcon } from 'lucide-vue-next';
import { useAbility } from '@casl/vue';
import { isZero, isClose } from '@/utils/is-zero';
import { resolveInvoiceCounterpartyType } from '@/utils/resolve-invoice-counterparty-type';
import CommentsPanel from '@/components/comments/CommentsPanel.vue';

const props = defineProps<{
  invoice: Invoice;
}>();

const { t } = useI18n();
const dateFormat = 'dd.MM.yy';
const ability = useAbility();

const showSelectLedgerEntries = ref(false);

const { data: invoiceAllocations } = useInvoiceAllocations(
  computed(() => props.invoice.id),
  ability.can('list', 'InvoiceAllocation'),
);
const allocatedAmount = computed(() => {
  return sumBy(invoiceAllocations.value ?? [], 'amount') / 100;
});
const toAllocate = computed(() => {
  return props.invoice.totalPositionSum - allocatedAmount.value;
});

const bookingId = computed(() => props.invoice.booking?.id ?? null);

const displayCounterpartyType = computed(() => resolveInvoiceCounterpartyType(props.invoice));

const { downloadInvoice, downloadInvoiceDirect, sendInvoiceToCustomerWithAlert, isSending } =
  useInvoicePdfs();

const showAllocations = ref(false);
</script>

<i18n lang="json">
{
  "en": {
    "status": "Status",
    "invoiceNumber": "Invoice Number",
    "invoiceDate": "Invoice Date",
    "total": "Total",
    "bookingNumber": "Booking Number",
    "receiver": "Receiver",
    "vatId": "VAT ID",
    "comment": "Comment",
    "invoiceNrNotAvailable": "INr. n/a",
    "downloadInvoice": "Show PDF",
    "downloadInvoiceDirect": "Download PDF",
    "sendToCustomer": "Send to Customer",
    "ledgerAllocations": "Ledger Allocations",
    "noBookingForAllocation": "No booking context for allocation.",
    "openAmount": "Open amount",
    "allocatedEntirely": "Allocated entirely",
    "allocateAmount": "Allocate Amount",
    "allocated": "Allocated",
    "invoiceType": "Invoice Type",
    "openBalance": "Open Balance"
  },
  "de": {
    "status": "Status",
    "invoiceNumber": "Rechnungsnummer",
    "invoiceDate": "Rechnungsdatum",
    "total": "Summe",
    "bookingNumber": "Buchungsnummer",
    "receiver": "Empfänger",
    "vatId": "USt-IdNr.",
    "comment": "Kommentar",
    "invoiceNrNotAvailable": "Rnr. n/a",
    "downloadInvoice": "Anzeigen (PDF)",
    "downloadInvoiceDirect": "Herunterladen (PDF)",
    "sendToCustomer": "An Kunde senden",
    "ledgerAllocations": "Forderungszuordnungen",
    "noBookingForAllocation": "Kein Buchungskontext für die Zuordnung.",
    "openAmount": "Noch offener Betrag",
    "allocatedEntirely": "Vollständig zugeordnet",
    "allocateAmount": "Betrag zuordnen",
    "allocated": "Zugewiesen",
    "invoiceType": "Rechnungstyp",
    "openBalance": "Offener Betrag"
  },
  "es": {
    "status": "Estado",
    "invoiceNumber": "Número de factura",
    "invoiceDate": "Fecha de factura",
    "total": "Total",
    "bookingNumber": "N.º de reserva",
    "receiver": "Destinatario",
    "comment": "Comentario",
    "invoiceNrNotAvailable": "N.º fact. s/d",
    "downloadInvoice": "Ver PDF",
    "downloadInvoiceDirect": "Descargar PDF",
    "sendToCustomer": "Enviar al cliente",
    "ledgerAllocations": "Imputaciones contables",
    "noBookingForAllocation": "Sin contexto de reserva para la imputación.",
    "openAmount": "Importe pendiente",
    "allocatedEntirely": "Totalmente imputado",
    "allocateAmount": "Imputar importe",
    "allocated": "Imputado",
    "invoiceType": "Tipo de factura",
    "openBalance": "Saldo pendiente"
  }
}
</i18n>
